import { Warning } from '@phosphor-icons/react';
import type { ProposedSlotClash } from '@/lib/useBarristerBusy';

/**
 * Warn (never block) when one or more PROPOSED conference slots clash with the assigned barrister's
 * diary. Used by the clerk propose flow, the barrister suggest flow and the brief-response flow.
 * The scheduler stays free to proceed — this only surfaces the conflict so the choice is informed.
 */
const fmtSlot = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export function ProposedSlotClashWarning({
  clashes,
  barristerLabel,
}: {
  clashes: ProposedSlotClash[];
  barristerLabel?: string;
}) {
  if (clashes.length === 0) return null;
  const who = barristerLabel ?? 'This barrister';
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
      <div className="flex items-center gap-1.5 font-medium">
        <Warning className="h-3.5 w-3.5" weight="fill" />
        {who} already has a commitment at {clashes.length === 1 ? 'this time' : 'some of these times'} — check before scheduling.
      </div>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {clashes.map((c) => (
          <li key={c.slot}>
            {fmtSlot(c.slot)} clashes with {c.events.map((e) => `${e.title} (${hhmm(e.startAt)}–${hhmm(e.endAt)})`).join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}
